import {CommonModule, formatDate} from '@angular/common';
import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faArrowCircleLeft, faHdd, faRotate, faVideo,} from '@fortawesome/free-solid-svg-icons';
import {HttpErrorResponse} from '@angular/common/http';
import {Chart, ChartConfiguration, ChartOptions, registerables} from 'chart.js';
import {BaseChartDirective} from 'ng2-charts';
import {ToastrService} from 'ngx-toastr';
import {forkJoin, of, Subject, takeUntil} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';

import {ApiService} from '../services/api.service';
import {AuthService} from '../services/auth.service';
import {RoleService} from '../services/role.service';
import {ReportingDatePipe} from '../shared/datetime/reporting-date.pipe';
import {
    DEFAULT_REPORTING_TIMEZONE,
    REPORTING_API_DATE_FORMAT,
    REPORTING_LOCALE,
} from '../shared/datetime/reporting-datetime.service';
import {VtePaginationComponent} from '../shared/vte/vte-pagination/vte-pagination.component';
import {
    buildTimelinePoints,
    ChartCoverageMeta,
    chartPagesToFetch,
    downsampleObservationsPreservingTransitions,
    HISTORY_CHART_MAX_DISPLAY_POINTS,
    HISTORY_CHART_MAX_PAGES,
    HISTORY_CHART_PAGE_SIZE,
    mergeObservationPages,
    timelineLegendKinds,
    TimelineStatusKind,
} from './device-health-history-timeline.helpers';
import {
    countDeviceHealthAttention,
    countDeviceHealthOffline,
    countDeviceHealthOnline,
    deviceHealthConnectionLabel,
    deviceHealthStatusTone,
} from './device-health-status.helpers';

Chart.register(...registerables);

export interface DeviceHealthRow {
    device_type: 'bridge' | 'camera' | string;
    device_id?: string | null;
    name: string;
    mapping_status?: string;
    primary_status?: string | null;
    organization_id?: number | null;
    organization_name?: string | null;
    connection_status?: string | null;
    cloud_connection_status?: string | null;
    edge_device_status?: string | null;
    kyra_bridge_id?: string | null;
    kyra_camera_id?: number | null;
}

export interface DeviceHealthResponse {
    status: 'available' | 'unavailable' | string;
    detail?: string;
    last_updated_at?: string | null;
    results?: DeviceHealthRow[];
}

export interface DeviceHealthHistoryObservation {
    id: number;
    device_type: string;
    device_key: string;
    device_id?: string | null;
    name: string;
    observation_kind: string;
    mapping_status?: string | null;
    primary_status?: string | null;
    connection_status?: string | null;
    cloud_connection_status?: string | null;
    edge_device_status?: string | null;
    observed_at?: string | null;
    organization_id?: number | null;
    organization_name?: string | null;
}

export interface DeviceHealthHistoryIncident {
    id: number;
    device_type: string;
    device_key: string;
    device_id?: string | null;
    name: string;
    incident_type: string;
    state: string;
    first_offline_at?: string | null;
    recovered_at?: string | null;
    closed_at?: string | null;
    organization_id?: number | null;
    organization_name?: string | null;
}

const HISTORY_TZ = DEFAULT_REPORTING_TIMEZONE;
const HISTORY_MAX_DAYS = 30;
const HISTORY_DEFAULT_DAYS = 7;

export type DeviceHealthMainTab = 'current' | 'history';

interface HistoryOrgOption {
    id: number;
    name: string;
}

@Component({
    selector: 'app-device-health',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        FontAwesomeModule,
        ReportingDatePipe,
        VtePaginationComponent,
        BaseChartDirective,
    ],
    templateUrl: './device-health.component.html',
    styleUrl: './device-health.component.css',
})
export class DeviceHealthComponent implements OnInit, OnDestroy {
    private readonly destroy$ = new Subject<void>();
    private historyRequestSeq = 0;
    private openIncidentRequestSeq = 0;

    @ViewChild(BaseChartDirective) timelineChart?: BaseChartDirective;

    isSuperUser = false;
    canViewHistory = false;
    canRefresh = false;
    loading = false;
    refreshing = false;
    activeTab: DeviceHealthMainTab = 'current';

    pageStatus: 'available' | 'unavailable' | 'error' = 'available';
    detailMessage = '';
    lastUpdatedAt: string | null = null;

    orgFilterId: number | null = null;
    orgFilterName = '';

    mappedBridges: DeviceHealthRow[] = [];
    mappedCameras: DeviceHealthRow[] = [];
    unassignedBridges: DeviceHealthRow[] = [];
    unassignedCameras: DeviceHealthRow[] = [];

    historyOrgOptions: HistoryOrgOption[] = [];
    historyOrgId: number | null = null;
    historyOrgLoading = false;
    historySelectedDeviceId: string | null = null;
    historyScopedDevices: DeviceHealthRow[] = [];
    historySnapshotLoading = false;
    openIncidentCount: number | null = null;
    openIncidentLoading = false;
    chartLoading = false;
    chartError = '';
    chartTruncated = false;
    chartDownsampled = false;
    chartCoverageNote = '';
    chartLegend: TimelineStatusKind[] = [];
    private timelineStatusLabels: string[] = [];
    timelineChartData: ChartConfiguration<'line'>['data'] = {labels: [], datasets: []};
    timelineChartOptions: ChartOptions<'line'> = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: {mode: 'index', intersect: false},
        plugins: {
            legend: {display: false},
            tooltip: {
                callbacks: {
                    label: (ctx) => {
                        const idx = ctx.dataIndex;
                        const status = this.timelineStatusLabels[idx];
                        if (ctx.parsed.y == null) {
                            return status || 'No observation (gap)';
                        }
                        return status || String(ctx.parsed.y);
                    },
                },
            },
        },
        scales: {
            x: {
                type: 'category',
                ticks: {maxRotation: 0, autoSkip: true, maxTicksLimit: 8},
                grid: {display: false},
            },
            y: {
                min: 0.5,
                max: 3.5,
                ticks: {
                    stepSize: 1,
                    callback: (value) => {
                        if (value === 3) {
                            return 'Online';
                        }
                        if (value === 2) {
                            return 'Other';
                        }
                        if (value === 1) {
                            return 'Offline';
                        }
                        return '';
                    },
                },
                grid: {color: '#e5e7eb'},
            },
        },
    };

    historyFromDate = '';
    historyToDate = '';
    historyValidationError = '';
    historyLoading = false;
    historyError = '';
    historyObservations: DeviceHealthHistoryObservation[] = [];
    historyIncidents: DeviceHealthHistoryIncident[] = [];
    historyObsPage = 1;
    historyObsPageSize = 50;
    historyObsTotal = 0;
    historyIncPage = 1;
    historyIncPageSize = 50;
    historyIncTotal = 0;

    constructor(
        private apiService: ApiService,
        private authService: AuthService,
        private roleService: RoleService,
        private route: ActivatedRoute,
        private router: Router,
        private toastr: ToastrService,
        library: FaIconLibrary,
    ) {
        library.addIcons(faHdd, faVideo, faRotate, faArrowCircleLeft);
        const defaults = this.defaultHistoryDateWindow();
        this.historyFromDate = defaults.from;
        this.historyToDate = defaults.to;
    }

    ngOnInit(): void {
        const userInfo = this.authService.getCurrentUserInfo();
        const groups = Array.isArray(userInfo?.role)
            ? userInfo.role
            : [userInfo?.role].filter(Boolean);
        this.isSuperUser = this.authService.isSuperUser();
        this.canViewHistory = this.roleService.canAccessDeviceHealthHistory(groups);
        this.canRefresh = this.roleService.canRefreshDeviceHealth(groups);

        this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
            const rawId = params['id'] ?? params['org_id'];
            const parsed = rawId != null && String(rawId).trim() !== ''
                ? Number(rawId)
                : null;
            this.orgFilterId = parsed != null && !Number.isNaN(parsed) ? parsed : null;
            this.orgFilterName = params['name'] ? String(params['name']) : '';

            const requestedTab = String(params['tab'] || '').toLowerCase();
            if (requestedTab === 'history') {
                if (this.canViewHistory) {
                    this.activeTab = 'history';
                    if (this.orgFilterId != null) {
                        this.historyOrgId = this.orgFilterId;
                    }
                } else {
                    this.activeTab = 'current';
                    void this.router.navigate([], {
                        relativeTo: this.route,
                        queryParams: {tab: null},
                        queryParamsHandling: 'merge',
                        replaceUrl: true,
                    });
                }
            } else {
                this.activeTab = 'current';
            }

            this.loadSnapshot();
            if (this.activeTab === 'history' && this.canViewHistory) {
                this.ensureHistoryOrganizationsLoaded();
                if (this.historyOrgId != null) {
                    this.loadOpenIncidentCount();
                }
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get showBackButton(): boolean {
        return this.isSuperUser && this.orgFilterId != null;
    }

    get visibleDevices(): DeviceHealthRow[] {
        return [
            ...this.mappedBridges,
            ...this.mappedCameras,
            ...this.unassignedBridges,
            ...this.unassignedCameras,
        ];
    }

    get hasAnyDevices(): boolean {
        return this.visibleDevices.length > 0;
    }

    get bridgeCount(): number {
        return this.mappedBridges.length + this.unassignedBridges.length;
    }

    get cameraCount(): number {
        return this.mappedCameras.length + this.unassignedCameras.length;
    }

    get totalDeviceCount(): number {
        return this.bridgeCount + this.cameraCount;
    }

    get onlineCount(): number {
        return countDeviceHealthOnline(this.visibleDevices);
    }

    /**
     * Connectivity-down devices (Offline, Bridge offline, Off).
     * Overlaps with Needs attention — an offline device is never dropped from this count.
     */
    get offlineCount(): number {
        return countDeviceHealthOffline(this.visibleDevices);
    }

    /**
     * Devices whose connection status needs attention (tone = bad).
     * Includes Offline connectivity labels plus Auth error, Error, etc.
     * Overlaps Offline; does not include Initializing, Busy, or Unknown.
     */
    get attentionCount(): number {
        return countDeviceHealthAttention(this.visibleDevices);
    }

    get needsAttention(): boolean {
        return this.attentionCount > 0;
    }

    get showSummary(): boolean {
        return this.pageStatus === 'available';
    }

    get lastUpdatedLabel(): string {
        if (!this.lastUpdatedAt) {
            return 'Not yet synchronized';
        }
        return this.formatRelativeTime(this.lastUpdatedAt);
    }

    get historyDeviceOptions(): DeviceHealthRow[] {
        return this.historyScopedDevices.filter((row) => !!row.device_id);
    }

    get selectedHistoryDevice(): DeviceHealthRow | null {
        if (!this.historySelectedDeviceId) {
            return null;
        }
        return this.historyDeviceOptions.find(
            (row) => String(row.device_id) === String(this.historySelectedDeviceId),
        ) || null;
    }

    get historyOnlineCount(): number {
        return countDeviceHealthOnline(this.historyScopedDevices);
    }

    get historyOfflineCount(): number {
        return countDeviceHealthOffline(this.historyScopedDevices);
    }

    selectMainTab(tab: DeviceHealthMainTab): void {
        if (tab === 'history' && !this.canViewHistory) {
            return;
        }
        this.activeTab = tab;
        const queryParams: Record<string, string | null> = {
            tab: tab === 'history' ? 'history' : null,
        };
        void this.router.navigate([], {
            relativeTo: this.route,
            queryParams,
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
        if (tab === 'history') {
            this.ensureHistoryOrganizationsLoaded();
            if (this.historyOrgId == null && this.orgFilterId != null) {
                this.historyOrgId = this.orgFilterId;
            }
            if (this.historyOrgId != null) {
                this.onHistoryOrganizationChange(false);
            }
        }
    }

    ensureHistoryOrganizationsLoaded(): void {
        if (!this.canViewHistory || this.historyOrgOptions.length > 0 || this.historyOrgLoading) {
            return;
        }
        this.historyOrgLoading = true;
        this.apiService.getOrganizations(1000).pipe(takeUntil(this.destroy$)).subscribe({
            next: (response: any) => {
                this.historyOrgLoading = false;
                const results = Array.isArray(response?.results) ? response.results : [];
                this.historyOrgOptions = results
                    .map((row: any) => ({
                        id: Number(row.id),
                        name: String(row.name || `Organization ${row.id}`),
                    }))
                    .filter((row: HistoryOrgOption) => !Number.isNaN(row.id));
            },
            error: () => {
                this.historyOrgLoading = false;
                this.historyOrgOptions = [];
            },
        });
    }

    onHistoryOrganizationChange(resetDevice = true): void {
        this.historyRequestSeq += 1;
        this.openIncidentRequestSeq += 1;
        if (resetDevice) {
            this.historySelectedDeviceId = null;
        }
        this.clearHistoryDetailState();
        this.clearTimelineChart();
        this.openIncidentCount = null;
        this.historyScopedDevices = [];
        if (this.historyOrgId == null) {
            return;
        }
        this.loadHistoryScopedSnapshot();
        this.loadOpenIncidentCount();
    }

    loadHistoryScopedSnapshot(): void {
        if (!this.canViewHistory || this.historyOrgId == null) {
            this.historyScopedDevices = [];
            return;
        }
        const orgId = this.historyOrgId;
        this.historySnapshotLoading = true;
        this.apiService.getDeviceHealth(orgId).pipe(takeUntil(this.destroy$)).subscribe({
            next: (response: DeviceHealthResponse) => {
                if (this.historyOrgId !== orgId) {
                    return;
                }
                this.historySnapshotLoading = false;
                if (!response || response.status !== 'available') {
                    this.historyScopedDevices = [];
                    return;
                }
                const results = Array.isArray(response.results) ? response.results : [];
                this.historyScopedDevices = results.filter(
                    (row) => row.mapping_status !== 'unmapped' && !!row.device_id,
                );
            },
            error: () => {
                if (this.historyOrgId !== orgId) {
                    return;
                }
                this.historySnapshotLoading = false;
                this.historyScopedDevices = [];
            },
        });
    }

    onHistoryDeviceChange(): void {
        this.clearHistoryDetailState();
        this.clearTimelineChart();
        if (!this.historySelectedDeviceId || this.historyOrgId == null) {
            return;
        }
        this.applyHistoryFilters();
    }

    loadOpenIncidentCount(): void {
        if (!this.canViewHistory || this.historyOrgId == null) {
            this.openIncidentCount = null;
            return;
        }
        const validation = this.validateHistoryDates();
        const window = validation
            ? this.defaultHistoryDateWindow()
            : {from: this.historyFromDate, to: this.historyToDate};
        const requestId = ++this.openIncidentRequestSeq;
        this.openIncidentLoading = true;
        this.apiService.getDeviceHealthHistoryIncidents({
            from_date: window.from,
            to_date: window.to,
            org_id: this.historyOrgId,
            state: 'open',
            page: 1,
            page_size: 1,
        }).pipe(takeUntil(this.destroy$)).subscribe({
            next: (response) => {
                if (requestId !== this.openIncidentRequestSeq) {
                    return;
                }
                this.openIncidentLoading = false;
                this.openIncidentCount = Number(response?.total_records) || 0;
            },
            error: () => {
                if (requestId !== this.openIncidentRequestSeq) {
                    return;
                }
                this.openIncidentLoading = false;
                this.openIncidentCount = null;
            },
        });
    }

    loadSnapshot(): void {
        this.loading = true;
        const orgId = this.isSuperUser ? this.orgFilterId : null;
        this.apiService.getDeviceHealth(orgId).pipe(takeUntil(this.destroy$)).subscribe({
            next: (response: DeviceHealthResponse) => {
                this.applyResponse(response);
                this.loading = false;
            },
            error: () => {
                this.pageStatus = 'error';
                this.detailMessage = 'Unable to load device health right now.';
                this.clearRows();
                this.loading = false;
            },
        });
    }

    refreshNow(): void {
        if (!this.canRefresh || this.refreshing) {
            return;
        }
        this.refreshing = true;
        this.apiService.refreshDeviceHealth().pipe(takeUntil(this.destroy$)).subscribe({
            next: (response: DeviceHealthResponse) => {
                this.refreshing = false;
                if (response?.status === 'available') {
                    this.toastr.success('Device health refreshed.', 'Success');
                } else {
                    this.toastr.warning(
                        'Device health is temporarily unavailable. Showing the last successful update if available.',
                        'Unavailable',
                    );
                }
                // Always reload GET so Super User org filter stays correct.
                this.loadSnapshot();
            },
            error: (error: HttpErrorResponse) => {
                this.refreshing = false;
                if (error.status === 409) {
                    this.toastr.warning(
                        'A device health sync is already running. Please try again shortly.',
                        'Refresh in progress',
                    );
                    return;
                }
                if (error.status === 403) {
                    this.toastr.error('You do not have permission to refresh device health.', 'Error');
                    return;
                }
                this.toastr.error('Unable to refresh device health right now.', 'Error');
            },
        });
    }

    clearOrgFilter(): void {
        if (!this.isSuperUser) {
            return;
        }
        this.router.navigate(['/device-health']);
    }

    backToOrganizations(): void {
        this.router.navigate(['/organization'], {state: {activeTab: 'customer'}});
    }

    applyHistoryFilters(): void {
        this.historyObsPage = 1;
        this.historyIncPage = 1;
        this.loadHistory();
    }

    resetHistoryFilters(): void {
        const defaults = this.defaultHistoryDateWindow();
        this.historyFromDate = defaults.from;
        this.historyToDate = defaults.to;
        this.historyValidationError = '';
        this.historyObsPage = 1;
        this.historyIncPage = 1;
        this.loadHistory();
    }

    onHistoryObsPageChange(page: number): void {
        this.historyObsPage = page;
        this.loadHistory();
    }

    onHistoryObsPageSizeChange(pageSize: number): void {
        this.historyObsPageSize = pageSize;
        this.historyObsPage = 1;
        this.loadHistory();
    }

    onHistoryIncPageChange(page: number): void {
        this.historyIncPage = page;
        this.loadHistory();
    }

    onHistoryIncPageSizeChange(pageSize: number): void {
        this.historyIncPageSize = pageSize;
        this.historyIncPage = 1;
        this.loadHistory();
    }

    loadHistory(): void {
        if (!this.canViewHistory) {
            this.clearHistoryDetailState();
            this.clearTimelineChart();
            return;
        }
        if (this.historyOrgId == null) {
            this.historyValidationError = '';
            this.historyError = '';
            this.clearHistoryDetailState();
            this.clearTimelineChart();
            this.historyLoading = false;
            return;
        }
        if (!this.historySelectedDeviceId) {
            this.historyValidationError = '';
            this.historyError = '';
            this.clearHistoryDetailState();
            this.clearTimelineChart();
            this.historyLoading = false;
            return;
        }

        const validation = this.validateHistoryDates();
        if (validation) {
            this.historyValidationError = validation;
            this.historyError = '';
            this.clearHistoryDetailState();
            this.clearTimelineChart();
            this.historyLoading = false;
            return;
        }
        this.historyValidationError = '';
        this.historyLoading = true;
        this.chartLoading = true;
        this.historyError = '';
        this.chartError = '';
        this.chartCoverageNote = '';
        const requestId = ++this.historyRequestSeq;
        const device = this.selectedHistoryDevice;
        const base = {
            from_date: this.historyFromDate,
            to_date: this.historyToDate,
            org_id: this.historyOrgId,
            device_id: this.historySelectedDeviceId || undefined,
            device_type: device?.device_type || undefined,
        };

        // Keep open-incident summary on the same Apply window as chart/tables.
        this.loadOpenIncidentCount();

        forkJoin({
            observations: this.apiService.getDeviceHealthHistoryObservations({
                ...base,
                page: this.historyObsPage,
                page_size: this.historyObsPageSize,
            }),
            chart: this.fetchChartObservationCoverage(base, requestId),
            incidents: this.apiService.getDeviceHealthHistoryIncidents({
                ...base,
                page: this.historyIncPage,
                page_size: this.historyIncPageSize,
            }),
        }).pipe(takeUntil(this.destroy$)).subscribe({
            next: ({observations, chart, incidents}) => {
                if (requestId !== this.historyRequestSeq) {
                    return;
                }
                this.historyLoading = false;
                this.chartLoading = false;
                this.historyObservations = Array.isArray(observations?.results)
                    ? observations.results
                    : [];
                this.historyIncidents = Array.isArray(incidents?.results)
                    ? incidents.results
                    : [];
                this.historyObsPage = observations?.page || this.historyObsPage;
                this.historyObsPageSize = observations?.page_size || this.historyObsPageSize;
                this.historyObsTotal = observations?.total_records || 0;
                this.historyIncPage = incidents?.page || this.historyIncPage;
                this.historyIncPageSize = incidents?.page_size || this.historyIncPageSize;
                this.historyIncTotal = incidents?.total_records || 0;
                this.applyTimelineFromObservations(chart.rows, chart.meta);
            },
            error: (error: HttpErrorResponse) => {
                if (requestId !== this.historyRequestSeq) {
                    return;
                }
                this.historyLoading = false;
                this.chartLoading = false;
                this.clearHistoryDetailState();
                this.clearTimelineChart();
                const apiDetail = error?.error?.detail;
                this.historyError = typeof apiDetail === 'string' && apiDetail.trim()
                    ? apiDetail
                    : 'Unable to load device health history right now.';
                this.chartError = this.historyError;
            },
        });
    }

    /**
     * Walk history observation pages (max page_size=100) up to HISTORY_CHART_MAX_PAGES.
     * Aborts early when requestId is superseded. Never invents samples.
     */
    private fetchChartObservationCoverage(
        base: {
            from_date: string;
            to_date: string;
            org_id: number | null;
            device_id?: string;
            device_type?: string;
        },
        requestId: number,
    ) {
        return this.apiService.getDeviceHealthHistoryObservations({
            ...base,
            page: 1,
            page_size: HISTORY_CHART_PAGE_SIZE,
        }).pipe(
            switchMap((first) => {
                if (requestId !== this.historyRequestSeq) {
                    return of({
                        rows: [] as DeviceHealthHistoryObservation[],
                        meta: {
                            totalRecords: 0,
                            fetchedCount: 0,
                            displayCount: 0,
                            hitFetchCap: false,
                            downsampled: false,
                            pagesFetched: 0,
                        } as ChartCoverageMeta,
                    });
                }
                const firstRows = Array.isArray(first?.results) ? first.results : [];
                const totalRecords = Number(first?.total_records) || 0;
                const reportedPages = Number(first?.total_pages) || 0;
                const totalPages = reportedPages > 0
                    ? reportedPages
                    : (totalRecords > 0
                        ? Math.ceil(totalRecords / HISTORY_CHART_PAGE_SIZE)
                        : (firstRows.length ? 1 : 0));
                const pageList = chartPagesToFetch(totalPages, HISTORY_CHART_MAX_PAGES);
                if (pageList.length <= 1) {
                    return of({
                        rows: firstRows as DeviceHealthHistoryObservation[],
                        meta: {
                            totalRecords,
                            fetchedCount: firstRows.length,
                            displayCount: firstRows.length,
                            hitFetchCap: totalRecords > firstRows.length,
                            downsampled: false,
                            pagesFetched: pageList.length || (firstRows.length ? 1 : 0),
                        } as ChartCoverageMeta,
                    });
                }
                const rest = pageList.slice(1).map((page) =>
                    this.apiService.getDeviceHealthHistoryObservations({
                        ...base,
                        page,
                        page_size: HISTORY_CHART_PAGE_SIZE,
                    }),
                );
                return forkJoin(rest).pipe(
                    map((pages) => {
                        const pageRows = [
                            firstRows,
                            ...pages.map((p) => (Array.isArray(p?.results) ? p.results : [])),
                        ];
                        const merged = mergeObservationPages(pageRows);
                        return {
                            rows: merged as DeviceHealthHistoryObservation[],
                            meta: {
                                totalRecords,
                                fetchedCount: merged.length,
                                displayCount: merged.length,
                                hitFetchCap: totalPages > HISTORY_CHART_MAX_PAGES
                                    || totalRecords > merged.length,
                                downsampled: false,
                                pagesFetched: pageList.length,
                            } as ChartCoverageMeta,
                        };
                    }),
                );
            }),
        );
    }

    private clearHistoryDetailState(): void {
        this.historyObservations = [];
        this.historyIncidents = [];
        this.historyObsTotal = 0;
        this.historyIncTotal = 0;
    }

    private clearTimelineChart(): void {
        this.timelineChartData = {labels: [], datasets: []};
        this.timelineStatusLabels = [];
        this.chartLegend = [];
        this.chartTruncated = false;
        this.chartDownsampled = false;
        this.chartCoverageNote = '';
        this.chartError = '';
        this.chartLoading = false;
    }

    private applyTimelineFromObservations(
        rows: DeviceHealthHistoryObservation[],
        meta: ChartCoverageMeta,
    ): void {
        const displayRows = downsampleObservationsPreservingTransitions(
            rows,
            HISTORY_CHART_MAX_DISPLAY_POINTS,
        );
        const downsampled = displayRows.length < rows.length;
        this.chartTruncated = meta.hitFetchCap;
        this.chartDownsampled = downsampled;
        this.chartCoverageNote = this.buildChartCoverageNote({
            ...meta,
            displayCount: displayRows.length,
            downsampled,
        });
        const points = buildTimelinePoints(displayRows);
        this.chartLegend = timelineLegendKinds(points);
        if (points.length === 0) {
            this.timelineChartData = {labels: [], datasets: []};
            this.timelineStatusLabels = [];
            return;
        }
        // API timestamps are UTC ISO; format once into Asia/Kolkata for axis labels.
        const labels = points.map((point) =>
            formatDate(new Date(point.t), 'dd-MMM HH:mm', REPORTING_LOCALE, HISTORY_TZ),
        );
        this.timelineStatusLabels = points.map((point) => point.label);
        this.timelineChartData = {
            labels,
            datasets: [
                {
                    data: points.map((point) => point.y),
                    label: 'Connection status',
                    borderColor: '#374151',
                    backgroundColor: 'transparent',
                    pointBackgroundColor: points.map((point) => {
                        if (point.kind === 'online') {
                            return '#15803d';
                        }
                        if (point.kind === 'offline') {
                            return '#b91c1c';
                        }
                        if (point.kind === 'gap') {
                            return '#9ca3af';
                        }
                        return '#d97706';
                    }),
                    pointBorderColor: '#ffffff',
                    pointRadius: points.map((point) => (point.kind === 'gap' ? 0 : 4)),
                    pointHoverRadius: 5,
                    spanGaps: false,
                    stepped: 'before',
                    tension: 0,
                    borderWidth: 2,
                },
            ],
        };
        queueMicrotask(() => this.timelineChart?.update());
    }

    private buildChartCoverageNote(meta: ChartCoverageMeta): string {
        const parts: string[] = [];
        if (meta.hitFetchCap) {
            parts.push(
                `Chart includes the newest ${meta.fetchedCount.toLocaleString('en-IN')} of `
                + `${meta.totalRecords.toLocaleString('en-IN')} observations in this window `
                + `(fetch capped at ${HISTORY_CHART_MAX_PAGES} pages × ${HISTORY_CHART_PAGE_SIZE}). `
                + 'Older samples in-range are omitted from the chart; use the observations table for full pagination.',
            );
        }
        if (meta.downsampled) {
            parts.push(
                `Display downsampled from ${meta.fetchedCount.toLocaleString('en-IN')} fetched `
                + `observations to ${meta.displayCount.toLocaleString('en-IN')} points, `
                + 'preserving endpoints and status transitions (gaps are not invented).',
            );
        }
        return parts.join(' ');
    }

    incidentTypeLabel(incidentType: string | null | undefined): string {
        if (incidentType === 'missing_inventory') {
            return 'Missing from inventory';
        }
        if (incidentType === 'offline') {
            return 'Offline';
        }
        return incidentType || 'Unknown';
    }

    observationKindLabel(kind: string | null | undefined): string {
        if (kind === 'inventory_absent') {
            return 'Missing from inventory sample';
        }
        if (kind === 'device') {
            return 'Device status';
        }
        return kind || 'Unknown';
    }

    /** Presentation tones only: healthy / attention / neutral. */
    statusTone(status: string | null | undefined): 'ok' | 'bad' | 'neutral' {
        return deviceHealthStatusTone(status);
    }

    displayStatus(status: string | null | undefined): string {
        if (status == null || String(status).trim() === '') {
            return 'Unknown';
        }
        return String(status);
    }

    connectionLabel(device: DeviceHealthRow): string {
        return deviceHealthConnectionLabel(device);
    }

    private applyResponse(response: DeviceHealthResponse | null | undefined): void {
        this.lastUpdatedAt = response?.last_updated_at ?? null;
        this.detailMessage = response?.detail || '';

        if (!response || response.status !== 'available') {
            this.pageStatus = 'unavailable';
            this.clearRows();
            return;
        }

        this.pageStatus = 'available';
        const results = Array.isArray(response.results) ? response.results : [];
        const bridges = results.filter((row) => row.device_type === 'bridge');
        const cameras = results.filter((row) => row.device_type === 'camera');

        const assignedBridges = bridges.filter((row) => row.mapping_status !== 'unmapped');
        const assignedCameras = cameras.filter((row) => row.mapping_status !== 'unmapped');
        if (this.isSuperUser) {
            this.mappedBridges = assignedBridges;
            this.mappedCameras = assignedCameras;
            this.unassignedBridges = bridges.filter((row) => row.mapping_status === 'unmapped');
            this.unassignedCameras = cameras.filter((row) => row.mapping_status === 'unmapped');
        } else {
            // Backend already excludes Unassigned for org users; keep a hard client filter.
            this.mappedBridges = assignedBridges;
            this.mappedCameras = assignedCameras;
            this.unassignedBridges = [];
            this.unassignedCameras = [];
        }
    }

    private clearRows(): void {
        this.mappedBridges = [];
        this.mappedCameras = [];
        this.unassignedBridges = [];
        this.unassignedCameras = [];
    }

    /** Same relative-time convention as Bridges pages. */
    private formatRelativeTime(value: string): string {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return 'Unknown';
        }
        const diffMs = Date.now() - date.getTime();
        const diffMinutes = Math.floor(diffMs / 60000);
        if (diffMinutes < 1) {
            return 'Just now';
        }
        if (diffMinutes < 60) {
            return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
        }
        const diffHours = Math.floor(diffMinutes / 60);
        if (diffHours < 24) {
            return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
        }
        const diffDays = Math.floor(diffHours / 24);
        return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    }

    defaultHistoryDateWindow(): {from: string; to: string} {
        const to = formatDate(new Date(), REPORTING_API_DATE_FORMAT, REPORTING_LOCALE, HISTORY_TZ);
        const toParts = to.split('-').map((part) => Number(part));
        const utcNoon = Date.UTC(toParts[0], toParts[1] - 1, toParts[2], 6, 30);
        const fromMs = utcNoon - (HISTORY_DEFAULT_DAYS - 1) * 24 * 60 * 60 * 1000;
        const from = formatDate(new Date(fromMs), REPORTING_API_DATE_FORMAT, REPORTING_LOCALE, HISTORY_TZ);
        return {from, to};
    }

    private validateHistoryDates(): string {
        if (!this.historyFromDate || !this.historyToDate) {
            return 'Select both From and To dates.';
        }
        if (!/^\d{4}-\d{2}-\d{2}$/.test(this.historyFromDate)
            || !/^\d{4}-\d{2}-\d{2}$/.test(this.historyToDate)) {
            return 'Dates must use YYYY-MM-DD.';
        }
        if (this.historyFromDate > this.historyToDate) {
            return 'From date cannot be after To date.';
        }
        const fromParts = this.historyFromDate.split('-').map(Number);
        const toParts = this.historyToDate.split('-').map(Number);
        const fromUtc = Date.UTC(fromParts[0], fromParts[1] - 1, fromParts[2]);
        const toUtc = Date.UTC(toParts[0], toParts[1] - 1, toParts[2]);
        const spanDays = Math.floor((toUtc - fromUtc) / (24 * 60 * 60 * 1000)) + 1;
        if (spanDays > HISTORY_MAX_DAYS) {
            return `Date range must not exceed ${HISTORY_MAX_DAYS} calendar days (${HISTORY_TZ}).`;
        }
        return '';
    }
}
